import { useContext, useEffect } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import {
  FluentProvider,
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
} from "@fluentui/react-components";
import { TeamsFxContext } from "./Context";
import Privacy from "./Privacy";
import TermsOfUse from "./TermsOfUse";
import Tab from "./Tab";
import Unauthorized from "./Unauthorized";
import AccessDenied from "./AccessDenied";

import { AuthProvider, useAuth } from "../context/AuthContext";
import { AuthenticatedRoute } from "../auth/AuthenticatedRoute";
import { FileTransferProvider } from "../context/FileTransferContext";
import { NotificationProvider } from "../context/NotificationContext";
import { FeedbackProvider } from "../context/FeedbackContext";
import { useTheme } from "../context/ThemeContext";

// The main app wrapped with the auth provider
export default function App() {
  const { themeString } = useContext(TeamsFxContext);

  return (
    <AuthProvider>
      <FileTransferProvider>
        <NotificationProvider>
          <FeedbackProvider>
            <AppContent themeString={themeString} />
          </FeedbackProvider>
        </NotificationProvider>
      </FileTransferProvider>
    </AuthProvider>
  );
}

// Separate component that consumes the auth context
function AppContent({ themeString }: { themeString: string }) {
  const { authMode } = useAuth();
  const { resolvedTheme, applyHostHint } = useTheme();

  // When running inside Teams, treat the host theme as the initial preference
  // for users who have never explicitly chosen one. The bridge re-runs whenever
  // Teams notifies us of a host theme change.
  const isTeamsEnvironment = authMode === 'teams';
  useEffect(() => {
    if (!isTeamsEnvironment) return;
    if (themeString === "dark") applyHostHint("dark");
    else if (themeString === "default") applyHostHint("light");
    // High-contrast is handled separately in getTheme(); we don't push it as a
    // light/dark hint.
  }, [isTeamsEnvironment, themeString, applyHostHint]);

  const getTheme = () => {
    if (isTeamsEnvironment && themeString === "contrast") {
      return teamsHighContrastTheme;
    }
    return resolvedTheme === "dark" ? teamsDarkTheme : teamsLightTheme;
  };

  return (
    <FluentProvider theme={getTheme()}>
      <Router>
        <Routes>
          {/* Common routes for both environments */}
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/termsofuse" element={<TermsOfUse />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/access-denied" element={<AccessDenied />} />


          {/* Teams-specific routes */}
          {isTeamsEnvironment ? (
            <>
              <Route path="/index.html" element={<Navigate to="/tab" replace />} />
              <Route path="/tab" element={
                <AuthenticatedRoute>
                  <Tab />
                </AuthenticatedRoute>
              } />
              <Route path="/" element={
                <AuthenticatedRoute>
                  <Tab />
                </AuthenticatedRoute>
              } />
            </>
          ) : (
            /* Web-only routes - consolidate all paths to root */
            <>
              <Route path="/" element={
                <AuthenticatedRoute>
                  <Tab />
                </AuthenticatedRoute>
              } />
              {/* Catch-all route to redirect any other path to root */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </Router>
    </FluentProvider>
  );
}